import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { Theme } from "@emotion/react";
import { mdsThemeHarvest } from "@/mds/themes/harvest";
import { mdsThemeHarvestMoon } from "@/mds/themes/harvest-moon";
import { MemCommonEditorTheme } from "@mem-labs/common-editor";

export type ThemeType = "classic" | "harvest" | "harvest-moon";

interface ThemeContextType {
  theme: Theme;
  themeType: ThemeType;
  toggleTheme: () => void;
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeType;
  getPersistedTheme?: () => ThemeType | null;
  persistTheme?: (theme: ThemeType) => void;
}

export const ThemeContextProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = "harvest",
  getPersistedTheme,
  persistTheme,
}) => {
  const [themeType, setThemeType] = useState<ThemeType>(() => {
    const value = getPersistedTheme?.();
    if (value && (value === "classic" || value === "harvest" || value === "harvest-moon")) {
      return value as ThemeType;
    }
    return defaultTheme;
  });

  const theme = themeType === "harvest" ? mdsThemeHarvest : mdsThemeHarvestMoon;

  // Persist theme changes to cookie
  useEffect(() => {
    persistTheme?.(themeType);
  }, [themeType, persistTheme]);

  // Set color-scheme CSS property on :root element based on theme
  useEffect(() => {
    const rootElement = document.documentElement;

    if (themeType === "harvest-moon") {
      // For "harvest-moon" theme, use dark mode
      rootElement.style.colorScheme = "dark";
    } else {
      // For "harvest" and "classic" themes, use light mode
      rootElement.style.colorScheme = "light";
    }

    // Cleanup function to reset on unmount
    return () => {
      rootElement.style.colorScheme = "";
    };
  }, [themeType]);

  const toggleTheme = () => {
    setThemeType(prev => (prev === "harvest" ? "harvest-moon" : "harvest"));
  };

  const setTheme = (newTheme: ThemeType) => {
    setThemeType(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    themeType,
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeContextProvider");
  }
  return context;
};

export const useMemCommonEditorTheme = (): MemCommonEditorTheme => {
  const { themeType } = useTheme();
  return themeType === "harvest-moon"
    ? MemCommonEditorTheme.HarvestMoon
    : MemCommonEditorTheme.Harvest;
};
